<script>
import { mapGetters } from 'vuex';
import { useVuelidate } from '@vuelidate/core';
import { useAlert } from 'dashboard/composables';
import { required } from '@vuelidate/validators';
import MangoAPI from 'dashboard/api/mango';
import router from '../../../../index';
import PageHeader from '../../SettingsSubPageHeader.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

export default {
  components: {
    PageHeader,
    NextButton,
  },
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      inboxName: '',
      apiKey: '',
      apiSalt: '',
      lineNumber: '',
      domain: '',
      lines: [],
      checks: [],
      isChecking: false,
      isSettingUp: false,
      setupSteps: [],
    };
  },
  computed: {
    ...mapGetters({
      uiFlags: 'inboxes/getUIFlags',
    }),
    // Creating an inbox with credentials MANGO rejects leaves a dead channel
    // behind, so the connection has to pass before the operator can continue.
    isConnectionVerified() {
      return (
        this.checks.length > 0 && !this.checks.some(c => c.status === 'error')
      );
    },
    showKeyHint() {
      return this.checks.some(c => c.key === 'api' && c.status === 'error');
    },
  },
  validations: {
    inboxName: { required },
    apiKey: { required },
    apiSalt: { required },
  },
  methods: {
    statusClass(status) {
      if (status === 'ok') return 'text-n-teal-11';
      if (status === 'error') return 'text-n-ruby-11';
      return 'text-n-amber-11';
    },
    checkTitle(key) {
      return this.$t(`INBOX_MGMT.ADD.MANGO_CHANNEL.CHECKS.${key.toUpperCase()}`);
    },
    stepTitle(key) {
      return this.$t(`INBOX_MGMT.ADD.MANGO_CHANNEL.STEPS.${key.toUpperCase()}`);
    },
    async checkConnection() {
      if (!this.apiKey.trim() || !this.apiSalt.trim()) return;
      this.isChecking = true;
      this.checks = [];
      try {
        const { data } = await MangoAPI.validateKey(
          this.apiKey.trim(),
          this.apiSalt.trim()
        );
        this.checks = data.checks || [];
        this.lines = data.lines || [];
        this.domain = data.domain || '';
        // One line is the common case — preselect it so there is nothing to click.
        if (!this.lineNumber && this.lines.length) {
          this.lineNumber = this.lines[0].number;
        }
      } catch (error) {
        useAlert(this.$t('INBOX_MGMT.ADD.MANGO_CHANNEL.CHECK_ERROR'));
      } finally {
        this.isChecking = false;
      }
    },
    async createChannel() {
      this.v$.$touch();
      if (this.v$.$invalid || !this.isConnectionVerified) return;

      try {
        const payload = await this.$store.dispatch('inboxes/createChannel', {
          name: this.inboxName.trim(),
          channel: {
            type: 'mango',
            api_key: this.apiKey.trim(),
            api_salt: this.apiSalt.trim(),
            vpbx_domain: this.domain,
            // MANGO reports lines as bare digits; store E.164 so call cards and
            // contacts show the same shape as everywhere else.
            virtual_phone_number: this.lineNumber.startsWith('+')
              ? this.lineNumber
              : `+${this.lineNumber}`,
          },
        });
        await this.runSetup(payload.id);
        useAlert(this.$t('INBOX_MGMT.ADD.MANGO_CHANNEL.ACTIVATED'));
        router.replace({
          name: 'settings_inboxes_add_agents',
          params: { page: 'new', inbox_id: payload.id },
        });
      } catch (error) {
        useAlert(
          error?.message || this.$t('INBOX_MGMT.ADD.MANGO_CHANNEL.CREATE_ERROR')
        );
      }
    },
    // Everything the client would otherwise configure by hand: read the PBX
    // directory and map agents to employees. Shown step by step, not as a
    // blank spinner. A failure here doesn't block the inbox — mapping can be
    // finished on the settings tab.
    async runSetup(inboxId) {
      this.isSettingUp = true;
      try {
        const { data } = await MangoAPI.setup(inboxId);
        this.setupSteps = data.steps || [];
      } catch (error) {
        this.setupSteps = [];
      } finally {
        this.isSettingUp = false;
      }
    },
  },
};
</script>

<template>
  <div class="h-full w-full p-6 col-span-6">
    <PageHeader
      :header-title="$t('INBOX_MGMT.ADD.MANGO_CHANNEL.TITLE')"
      :header-content="$t('INBOX_MGMT.ADD.MANGO_CHANNEL.DESC')"
    />
    <form class="flex flex-wrap flex-col mx-0" @submit.prevent="createChannel">
      <div class="flex-shrink-0 flex-grow-0">
        <label :class="{ error: v$?.inboxName?.$error }">
          {{ $t('INBOX_MGMT.ADD.MANGO_CHANNEL.INBOX_NAME.LABEL') }}
          <input
            v-model="inboxName"
            type="text"
            :placeholder="
              $t('INBOX_MGMT.ADD.MANGO_CHANNEL.INBOX_NAME.PLACEHOLDER')
            "
            @blur="v$?.inboxName?.$touch"
          />
        </label>
      </div>

      <div class="flex-shrink-0 flex-grow-0 mt-4">
        <label :class="{ error: v$?.apiKey?.$error }">
          {{ $t('INBOX_MGMT.ADD.MANGO_CHANNEL.API_KEY.LABEL') }}
          <input
            v-model="apiKey"
            type="text"
            :placeholder="
              $t('INBOX_MGMT.ADD.MANGO_CHANNEL.API_KEY.PLACEHOLDER')
            "
            @blur="v$?.apiKey?.$touch"
          />
        </label>
      </div>

      <div class="flex-shrink-0 flex-grow-0 mt-4">
        <label :class="{ error: v$?.apiSalt?.$error }">
          {{ $t('INBOX_MGMT.ADD.MANGO_CHANNEL.API_SALT.LABEL') }}
          <input
            v-model="apiSalt"
            type="text"
            :placeholder="
              $t('INBOX_MGMT.ADD.MANGO_CHANNEL.API_SALT.PLACEHOLDER')
            "
            @blur="v$?.apiSalt?.$touch"
          />
        </label>
        <p class="mt-1 text-xs text-n-slate-11">
          {{ $t('INBOX_MGMT.ADD.MANGO_CHANNEL.API_SALT.HINT') }}
        </p>
        <NextButton
          type="button"
          faded
          blue
          sm
          class="mt-2"
          :is-loading="isChecking"
          :label="$t('INBOX_MGMT.ADD.MANGO_CHANNEL.CHECK_BUTTON')"
          @click="checkConnection"
        />
      </div>

      <ul v-if="checks.length" class="mt-4 mb-0 list-none pl-0">
        <li
          v-for="check in checks"
          :key="check.key"
          class="flex items-baseline gap-2 py-1 text-sm"
        >
          <span :class="statusClass(check.status)">●</span>
          <span>{{ checkTitle(check.key) }}</span>
          <span class="text-n-slate-11">— {{ check.message }}</span>
        </li>
      </ul>

      <div
        v-if="showKeyHint"
        class="mt-3 p-3 rounded-lg bg-n-alpha-2 text-xs text-n-slate-11 leading-relaxed"
      >
        <p class="font-medium text-n-slate-12 mb-1">
          {{ $t('INBOX_MGMT.ADD.MANGO_CHANNEL.KEY_HELP.TITLE') }}
        </p>
        <p class="mb-1">
          {{ $t('INBOX_MGMT.ADD.MANGO_CHANNEL.KEY_HELP.STEP_1') }}
        </p>
        <p class="mb-1">
          {{ $t('INBOX_MGMT.ADD.MANGO_CHANNEL.KEY_HELP.STEP_2') }}
        </p>
        <p>{{ $t('INBOX_MGMT.ADD.MANGO_CHANNEL.KEY_HELP.STEP_3') }}</p>
      </div>

      <div v-if="lines.length" class="flex-shrink-0 flex-grow-0 mt-4">
        <label>
          {{ $t('INBOX_MGMT.ADD.MANGO_CHANNEL.LINE.LABEL') }}
          <select v-model="lineNumber">
            <option v-for="line in lines" :key="line.number" :value="line.number">
              {{ line.number }}{{ line.name ? ` — ${line.name}` : '' }}
            </option>
          </select>
        </label>
        <p class="mt-1 text-xs text-n-slate-11">
          {{ $t('INBOX_MGMT.ADD.MANGO_CHANNEL.LINE.HINT') }}
        </p>
      </div>

      <ul v-if="setupSteps.length" class="mt-4 mb-0 list-none pl-0">
        <li
          v-for="step in setupSteps"
          :key="step.key"
          class="flex items-baseline gap-2 py-1 text-sm"
        >
          <span :class="statusClass(step.status)">●</span>
          <span>{{ stepTitle(step.key) }}</span>
          <span class="text-n-slate-11">— {{ step.message }}</span>
        </li>
      </ul>

      <div class="w-full mt-4">
        <NextButton
          :is-loading="uiFlags.isCreating || isSettingUp"
          :disabled="!isConnectionVerified"
          type="submit"
          solid
          blue
          :label="$t('INBOX_MGMT.ADD.MANGO_CHANNEL.SUBMIT_BUTTON')"
        />
        <p v-if="!isConnectionVerified" class="mt-2 text-xs text-n-slate-11">
          {{ $t('INBOX_MGMT.ADD.MANGO_CHANNEL.VERIFY_FIRST') }}
        </p>
      </div>
    </form>
  </div>
</template>
